import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, KeyRound, LogOut, ShieldAlert } from 'lucide-react';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_source-scout-4/artifacts/hca8zcbd_WhatsApp%20Image%202026-02-28%20at%2022.18.51.jpeg";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (!token || !storedUser) {
      navigate('/admin/login', { replace: true });
      return;
    }

    try {
      const user = JSON.parse(storedUser);
      if (!user.must_change_password) navigate('/admin', { replace: true });
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/admin/login', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must contain at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        { new_password: newPassword, confirm_password: confirmPassword },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      let email = '';
      try {
        email = JSON.parse(localStorage.getItem('user') || '{}').email || '';
      } catch {
        // The login page can still be shown without pre-filling the email.
      }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/admin/login', {
        replace: true,
        state: { passwordChanged: true, email },
      });
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(err.response?.data?.detail || 'Unable to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('token');
    try {
      if (token) {
        await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch {
      // A network problem must not trap the user on this screen.
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/admin/login', { replace: true });
    }
  };

  return (
    <div className="min-h-screen hero-bg flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Tejas Info Solutions" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white uppercase tracking-wider">Change Password</h1>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-sm p-8 border border-slate-700">
          <div className="flex gap-3 bg-orange-500/10 border border-orange-500/30 text-orange-300 px-4 py-3 mb-6">
            <ShieldAlert className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Password change required</p>
              <p className="text-sm text-slate-300 mt-1">
                This is your first login. Replace the temporary password before continuing.
              </p>
            </div>
          </div>

          {error && (
            <div data-testid="change-password-error" className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 mb-6">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="change-password-form">
            <div className="mb-5">
              <label className="block text-slate-400 text-sm uppercase tracking-wider mb-2">New Password</label>
              <div className="relative">
                <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="Minimum 8 characters"
                  data-testid="new-password-input"
                  className="w-full bg-slate-900/50 border-2 border-slate-700 text-white placeholder-slate-500 pl-12 pr-12 py-4 focus:border-orange-500 focus:outline-none"
                  required
                />
                <button type="button" onClick={() => setShowPassword(value => !value)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300" aria-label="Toggle password visibility">
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-slate-400 text-sm uppercase tracking-wider mb-2">Confirm New Password</label>
              <div className="relative">
                <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="Enter the password again"
                  data-testid="confirm-password-input"
                  className="w-full bg-slate-900/50 border-2 border-slate-700 text-white placeholder-slate-500 pl-12 pr-4 py-4 focus:border-orange-500 focus:outline-none"
                  required
                />
              </div>
            </div>

            <button type="submit" disabled={loading} data-testid="change-password-submit" className="w-full btn-lift bg-orange-500 text-white font-bold tracking-wider uppercase px-8 py-4 hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Updating Password...' : 'Change Password & Return to Login'}
            </button>
          </form>

          <button type="button" onClick={handleLogout} className="w-full mt-4 flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-white">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
};
